import { api } from "@/lib/api/client";

export type RecordingPublicationStatus =
  | "withheld"
  | "published"
  | "withdrawn"
  | "superseded";

export type RecordingTransitionAction =
  | "withhold"
  | "publish"
  | "correct"
  | "withdraw"
  | "supersede";

export interface RecordingDestinationReview {
  destination_reviewed_at?: string;
  destination_reviewed_by?: string;
  authenticated: boolean;
  no_chat: boolean;
  no_credentials: boolean;
  no_admin_controls: boolean;
}

export interface RecordingNoticeReceipt {
  version: string;
  status: "confirmed" | "opted_out" | "approved_exception";
  recorded_at: string;
  recorded_by: string;
}

export interface TeachingRecordingArtifact {
  placement_id: string | null;
  artifact_id: string;
  file_type: string;
  provider_recording_id: string;
  provider_state: string;
  publication_status: RecordingPublicationStatus | null;
  revision: number | null;
  audience_rule: string | null;
  destination_reviewed_at: string | null;
  destination_reviewed_by: string | null;
  destination_authenticated: boolean | null;
  destination_no_chat: boolean | null;
  destination_no_credentials: boolean | null;
  destination_no_admin_controls: boolean | null;
  published_at: string | null;
  withdrawn_at: string | null;
  display_label: string;
}

export type RecordingCustody = "provider_discovery" | "provider_link";

/** The session's recording link. The URL is never in a projection: `review` returns it. */
export interface TeachingRecordingLink {
  link_id: string;
  provider: "zoom_share" | "drive_file";
  provider_word: string;
  entered_at: string;
  entered_by: string;
  placement_id: string | null;
  publication_status: RecordingPublicationStatus | null;
  revision: number | null;
  audience_rule: string | null;
  destination_reviewed_at: string | null;
  destination_reviewed_by: string | null;
  destination_authenticated: boolean | null;
  destination_no_chat: boolean | null;
  destination_no_credentials: boolean | null;
  destination_no_admin_controls: boolean | null;
  published_at: string | null;
  withdrawn_at: string | null;
  display_label: string | null;
}

export interface TeachingSessionRecordings {
  role: "teaching";
  session_id: string;
  notice: RecordingNoticeReceipt | null;
  artifacts: TeachingRecordingArtifact[];
  /** Null for a tenant with no recording journey. */
  custody: RecordingCustody | null;
  /** The provider's sign-in rule on recordings is attested; a link cannot publish before. */
  signin_required: boolean;
  audience_sentence: string | null;
  /** The session has ended and is not cancelled: a link may be added. */
  link_addable: boolean;
  link: TeachingRecordingLink | null;
}

export interface LearnerPublishedRecording {
  placement_id: string;
  artifact_id: string;
  file_type: string;
  publication_status: "published";
  revision: number;
  published_at: string;
  display_label: string;
  destination_available: boolean;
  link_id: string | null;
  target_kind: "artifact" | "link";
  provider: string;
  provider_word: string;
}

export interface LearnerWithdrawnRecording {
  placement_id: string;
  publication_status: "withdrawn";
  withdrawn_at: string;
  display_label: string;
}

export interface LearnerSessionRecordings {
  role: "learner";
  session_id: string;
  notice: Pick<RecordingNoticeReceipt, "version" | "status"> | null;
  audience_sentence: string | null;
  recordings: LearnerPublishedRecording[];
  withdrawn: LearnerWithdrawnRecording[];
}

export type SessionRecordings = TeachingSessionRecordings | LearnerSessionRecordings;

export interface RecordingHistoryEvent {
  event_no: number;
  action: RecordingTransitionAction;
  prior_status: RecordingPublicationStatus | null;
  publication_status: RecordingPublicationStatus;
  prior_artifact_id: string | null;
  artifact_id: string;
  audience_rule: string;
  reason: string | null;
  recorded_by: string;
  created_at: string;
  destination_reviewed_at: string | null;
  destination_authenticated: boolean;
  destination_no_chat: boolean;
  destination_no_credentials: boolean;
  destination_no_admin_controls: boolean;
}

export interface RecordingTransitionInput {
  action: RecordingTransitionAction;
  revision: number | null;
  display_label?: string;
  /** Why the recording is withdrawn; required for a withdrawal (ADR 0028). */
  reason?: string;
  replacement_target_id?: string;
  destination_review?: {
    authenticated: true;
    no_chat: true;
    no_credentials: true;
    no_admin_controls: true;
  };
}

export type RecordingDestination = {
  kind: "provider_playback" | "share_link";
  url: string;
  label: string;
};

export type RecordingTransitionResult =
  | { command_outcome: "confirmed"; projection: SessionRecordings }
  | {
      command_outcome: "confirmed";
      placement: {
        placement_id: string;
        artifact_id: string;
        publication_status: RecordingPublicationStatus;
        revision: number;
        audience_rule: string;
        published_at: string | null;
        withdrawn_at: string | null;
        display_label: string;
      };
    };

export function sessionRecordingsQueryKey(
  sessionId: string,
  identityKey: string,
): readonly unknown[] {
  return ["session-recordings", identityKey, sessionId];
}

const sessionPath = (sessionId: string) => `/sessions/${encodeURIComponent(sessionId)}/recordings`;

export const sessionRecordingsApi = {
  get: (sessionId: string) => api.get<SessionRecordings>(sessionPath(sessionId)),
  recordNotice: (
    sessionId: string,
    version: string,
    status: RecordingNoticeReceipt["status"],
  ) => api.post<RecordingNoticeReceipt>(`/sessions/${encodeURIComponent(sessionId)}/recording-notice`, {
    version,
    status,
  }),
  transition: (
    sessionId: string,
    artifactId: string,
    input: RecordingTransitionInput,
  ) => api.post<RecordingTransitionResult>(
    `${sessionPath(sessionId)}/${encodeURIComponent(artifactId)}/transition`,
    input,
  ),
  /** Enter the session's recording link; it is saved Not published. */
  addLink: (sessionId: string, url: string) => api.post<{
    command_outcome: "confirmed";
    projection?: SessionRecordings;
    placement?: unknown;
  }>(`/sessions/${encodeURIComponent(sessionId)}/recording-link`, { url }),
  review: (sessionId: string, placementId: string) => api.post<RecordingDestination>(
    `${sessionPath(sessionId)}/${encodeURIComponent(placementId)}/review`,
    {},
  ),
  open: (sessionId: string, placementId: string) => api.post<RecordingDestination>(
    `${sessionPath(sessionId)}/${encodeURIComponent(placementId)}/open`,
    {},
  ),
  history: (sessionId: string, placementId: string) => api.get<RecordingHistoryEvent[]>(
    `${sessionPath(sessionId)}/${encodeURIComponent(placementId)}/history`,
  ),
};
